import { StrictMode } from 'react'
import { createRoot, type Root } from 'react-dom/client'
import { App } from './app.tsx'
import { resolveApiBaseUrl } from './lib/api-origin.ts'
import { createRuntime } from './lib/runtime.ts'
import { createAppRouter } from './routes.tsx'
import { themeStore } from './theme/theme-store.ts'
import './styles.css'

/**
 * The entry point, and the only module in this application with side effects.
 *
 * The runtime — the session store, the query client, and the generated client pointed at
 * an API — is created **here, at module scope**, once, before React renders anything.
 * That is the whole of docs/ux/PLATFORM-ADMIN.md §5.3's "one query client, created once,
 * and it is the configured one" (**CF1**): a component cannot rebuild what it was handed.
 *
 * Which API it talks to comes from the hostname this page was served on
 * (`lib/api-origin.ts`), and the environment word in the masthead comes from that API's
 * own `/health` — never from anything compiled into this bundle (§5.1).
 */
const runtime = createRuntime({
  apiBaseUrl: resolveApiBaseUrl(window.location, import.meta.env.VITE_API_BASE_URL),
})

/*
 * The colour scheme the owner chose, applied again here. `index.html`'s inline script has already set
 * it before the first paint; this is what makes the choice hold if that script did not run, and what
 * listens for the choice being changed in another tab (P09).
 */
themeStore.start()

const router = createAppRouter({ session: runtime.session })

/**
 * Mounts the application into `container`, and hands back the root it created.
 *
 * **The return value is the point.** `createRoot(container).render(…)` starts a React root
 * that nothing afterwards can stop: it keeps scheduling work on the scheduler's macrotask
 * queue, and a caller holding no reference to it has no way to say "you are finished". In
 * the browser that costs nothing, because the page outlives the root. In a test it is the
 * defect this signature exists to prevent — see the note in `main.test.tsx`.
 */
export function mount(container: Element): Root {
  const root = createRoot(container)
  root.render(
    <StrictMode>
      <App runtime={runtime} router={router} />
    </StrictMode>,
  )
  return root
}

const container = document.getElementById('root')

/**
 * The root this module mounted into the element `index.html` provides, or `null` when the
 * page had none.
 *
 * This module is imported for its side effects, so an export is the only handle anything
 * can have on what those side effects started.
 */
export const root: Root | null = container === null ? null : mount(container)
